/* eslint-disable react/jsx-no-bind */
import _ from 'lodash-es';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack, CheckCircleOutline, Delete } from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useConfirm } from 'material-ui-confirm';
import adminFormStyles from '../../shared/styles/adminForm.styles';
import styleColors from '../../colors.styles';
import { AlertsContext } from '../../shared/alerts/Alerts.context';
import TemplatesApi from '../Templates.api';
import TemplateContentForm from '../TemplateContentForm';
import TemplateTargetingForm from '../TemplateTargetingForm';
import { initTargeting, targetingFromAPI } from '../TemplateTargeting.utils';
import {
  applyCategoryChange,
  buildTemplatePayload,
  getCategoryChangeErrors,
  getDateValidationError,
  hasTemplateChanges,
  shouldShowEndNowButton,
  toCentralEndOfDay,
  toCentralStartOfDay,
  validateTemplate,
} from '../Template.validation';
import {
  DEFAULT_TEMPLATE,
  DEFAULT_TOPIC,
  getInitialDate,
  SHOPPABLE_CATEGORY_ID,
  TEMPLATE_CATEGORIES,
} from '../Template.model';

const styles = {
  ...adminFormStyles,
  backIcon: {
    fontSize: '14px !important',
  },
  classificationField: {
    flex: 1,
    minWidth: 0,
  },
  selectField: {
    ...adminFormStyles.field,
    '& .MuiSelect-select': {
      padding: '12px 16px',
    },
  },
  evergreenButton: {
    backgroundColor: 'transparent',
    borderColor: '#dbe2e6',
    borderRadius: '20px',
    borderWidth: '1px',
    color: '#656d80',
    fontSize: '13px',
    fontWeight: 700,
    lineHeight: 1.2,
    minHeight: 28,
    px: 1.25,
    py: 0.25,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: styleColors.base.barely_blue,
      borderColor: styleColors.base.walmart_blue,
      color: styleColors.base.walmart_blue,
    },
  },
  evergreenButtonSelected: {
    backgroundColor: styleColors.base.walmart_blue,
    borderColor: styleColors.base.walmart_blue,
    color: styleColors.base.light,
    '&:hover': {
      backgroundColor: styleColors.base.walmart_blue,
      borderColor: styleColors.base.walmart_blue,
      color: styleColors.base.light,
    },
  },
  schedulingGrid: {
    columnGap: 2,
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
    rowGap: 0.75,
  },
  schedulingLabelRow: {
    alignItems: 'center',
    display: 'flex',
    minHeight: 28,
  },
  schedulingEndLabelRow: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    minHeight: 28,
  },
  evergreenField: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      backgroundColor: styleColors.base.barely_blue,
      borderRadius: '8px',
      '& fieldset': {
        borderColor: styleColors.base.walmart_blue,
        borderStyle: 'dashed',
      },
      '&:hover fieldset': {
        borderColor: styleColors.base.walmart_blue,
        borderStyle: 'dashed',
      },
    },
    '& .MuiOutlinedInput-input': {
      color: styleColors.base.walmart_blue,
      fontSize: '14px',
      padding: '12px 16px',
      WebkitTextFillColor: styleColors.base.walmart_blue,
    },
    '& .Mui-disabled': {
      WebkitTextFillColor: styleColors.base.walmart_blue,
    },
  },
  dateField: {
    ...adminFormStyles.field,
    width: '100%',
  },
  endNowButton: {
    color: '#041f41',
    fontSize: '14px',
    fontWeight: 700,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: 'transparent',
      color: 'red',
    },
  },
};

export default function TemplateForm({
  initialTemplate,
  topics,
  onBack,
  onSaved,
  onTopicsChanged,
}) {
  const isEditing = Boolean(initialTemplate?.id);
  const confirm = useConfirm();
  const { showAlert } = useContext(AlertsContext);

  const [template, setTemplate] = useState({
    ...DEFAULT_TEMPLATE,
    ...initialTemplate,
  });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [evergreen, setEvergreen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [addTopic, setAddTopic] = useState(false);
  const [addSubtopic, setAddSubtopic] = useState(false);
  const [newTopic, setNewTopic] = useState('');
  const [newSubtopic, setNewSubtopic] = useState('');
  const [localTopics, setLocalTopics] = useState(topics);
  const [topicLoading, setTopicLoading] = useState(false);
  const [subtopicLoading, setSubtopicLoading] = useState(false);
  const [targeting, setTargeting] = useState(
    initialTemplate?.targeting
      ? targetingFromAPI(initialTemplate.targeting)
      : initTargeting()
  );
  const [targetingValid, setTargetingValid] = useState(true);
  const [isValidating, setIsValidating] = useState(false);

  const initialTargetingValue = useMemo(
    () =>
      initialTemplate?.targeting
        ? targetingFromAPI(initialTemplate.targeting)
        : initTargeting(),
    [initialTemplate]
  );

  const handleTargetingChanged = useCallback((newState, isValid) => {
    setTargeting(newState);
    setTargetingValid(isValid);
  }, []);

  const topicsMap = useMemo(() => _.keyBy(localTopics, 'id'), [localTopics]);
  const subtopics = topicsMap[template.topic]?.subtopics ?? [];
  const isShoppableSelected = template.category === SHOPPABLE_CATEGORY_ID;

  useEffect(() => {
    setLocalTopics(topics);
  }, [topics]);

  useEffect(() => {
    if (initialTemplate) {
      setTemplate({ ...DEFAULT_TEMPLATE, ...initialTemplate });
      setStartDate(getInitialDate(initialTemplate.start_date));
      setEndDate(getInitialDate(initialTemplate.end_date));
      setEvergreen(initialTemplate.end_date === null);
      setTargeting(
        initialTemplate.targeting
          ? targetingFromAPI(initialTemplate.targeting)
          : initTargeting()
      );
    }
  }, [initialTemplate]);

  function validateForm() {
    const result = validateTemplate({
      template,
      startDate,
      endDate,
      evergreen,
      existingErrors: errors,
    });
    setErrors(result.errors);
    return result.isValid;
  }

  function handleCategoryChange(event) {
    const newCategory = event.target.value;
    setTemplate((currentTemplate) =>
      applyCategoryChange(currentTemplate, newCategory)
    );
    setErrors((prev) => getCategoryChangeErrors(prev, newCategory));
  }

  async function onSubmit() {
    setIsValidating(true);
    if (!validateForm() || !targetingValid) {
      setIsValidating(false);
      return;
    }

    setLoading(true);
    try {
      const { payload, error } = buildTemplatePayload({
        template,
        startDate,
        endDate,
        evergreen,
        targeting,
        topics: localTopics,
      });

      if (error) {
        showAlert(error, 'error');
        return;
      }

      if (isEditing) {
        await TemplatesApi.editTemplate(initialTemplate.id, payload);
      } else {
        await TemplatesApi.createTemplate(payload);
      }

      showAlert(
        `Template ${isEditing ? 'updated' : 'created'} successfully!`,
        'success'
      );
      onSaved();
    } catch (err) {
      console.error(
        `Error ${isEditing ? 'updating' : 'creating'} Template:`,
        err
      );
      showAlert(
        `Failed to ${isEditing ? 'update' : 'create'} Template`,
        'error'
      );
    } finally {
      setLoading(false);
      setIsValidating(false);
    }
  }

  async function handleCreateTopic() {
    const trimmedNewTopicName = newTopic.trim().toLowerCase();
    if (!trimmedNewTopicName) return;

    const existing = localTopics.find(
      (topic) => topic.name.trim().toLowerCase() === trimmedNewTopicName
    );

    if (existing) {
      setTemplate((prev) => ({
        ...prev,
        topic: existing.id,
        subtopic: '',
      }));
      setAddTopic(false);
      setNewTopic('');
      return;
    }

    setTopicLoading(true);
    try {
      const topicPayload = {
        ...DEFAULT_TOPIC,
        name: newTopic,
      };
      const response = await TemplatesApi.createTopic(topicPayload);
      const createdTopic = response.data.data[0];
      setLocalTopics((prevTopics) => [...prevTopics, createdTopic]);
      setTemplate((prevTemplate) => ({
        ...prevTemplate,
        topic: createdTopic.id,
        subtopic: '',
      }));
      setAddTopic(false);
      setNewTopic('');
      await onTopicsChanged();
    } catch (err) {
      console.error('Error creating topic:', err);
      showAlert('Failed to create topic', 'error');
    } finally {
      setTopicLoading(false);
    }
  }

  async function handleCreateSubtopic() {
    const trimmedName = newSubtopic.trim().toLowerCase();
    if (!trimmedName) return;

    const topicObj = topicsMap[template.topic];
    if (!topicObj) {
      showAlert('Cannot create subtopic without a selected topic.', 'error');
      return;
    }

    const existing = (topicObj.subtopics || []).find(
      (subtopic) => subtopic.name.trim().toLowerCase() === trimmedName
    );
    if (existing) {
      setTemplate((prev) => ({
        ...prev,
        subtopic: existing.id,
      }));
      setAddSubtopic(false);
      setNewSubtopic('');
      return;
    }

    setSubtopicLoading(true);
    try {
      const subtopicPayload = [{ name: newSubtopic }];
      const response = await TemplatesApi.createSubtopic({
        topicId: topicObj.id,
        subtopics: subtopicPayload,
      });
      const createdSubtopic = response.data.data[0];
      setLocalTopics((prevTopics) =>
        prevTopics.map((topic) =>
          topic.id === topicObj.id
            ? {
                ...topic,
                subtopics: [...(topic.subtopics || []), createdSubtopic],
              }
            : topic
        )
      );
      setTemplate((prevTemplate) => ({
        ...prevTemplate,
        subtopic: createdSubtopic.id,
      }));
      setAddSubtopic(false);
      setNewSubtopic('');
      await onTopicsChanged();
    } catch (err) {
      console.error('Error creating subtopic:', err);
      showAlert('Failed to create subtopic', 'error');
    } finally {
      setSubtopicLoading(false);
    }
  }

  function hasChanges() {
    return hasTemplateChanges({
      template,
      initialTemplate,
      targeting,
      startDate,
      endDate,
      evergreen,
    });
  }

  async function handleCancel() {
    if (!hasChanges()) {
      onBack();
      return;
    }

    try {
      await confirm({
        title: 'Are you sure you want to go back?',
        description: isEditing
          ? 'Your unsaved changes will be lost.'
          : 'By leaving this page, you will lose your progress',
        confirmationText: 'Yes',
        cancellationText: 'No',
      });
      onBack();
    } catch {
      // User chose to stay on the page.
    }
  }

  async function handleDeleteTemplate() {
    try {
      await confirm({
        title: 'Are you sure you want to end this template?',
        description: 'This will remove the template from view in the MLS app.',
        confirmationText: 'Yes',
        cancellationText: 'No',
      });
    } catch {
      // User chose not to end the template.
      return;
    }

    setLoading(true);
    try {
      await TemplatesApi.deleteTemplate(template.id);
      showAlert('The template has been successfully ended.', 'success');
      onSaved();
    } catch (err) {
      console.error('Error ending template:', err);
      showAlert('Failed to end template.', 'error');
    } finally {
      setLoading(false);
    }
  }

  function handleDateError(key, errorCode) {
    if (!errorCode) {
      setErrors((prev) => _.omit(prev, key));
      return;
    }

    const errorMsg = getDateValidationError(key, errorCode);
    if (errorMsg) {
      setErrors((prev) => ({ ...prev, [key]: errorMsg }));
    }
  }

  function renderError(key) {
    return (
      errors[key] && (
        <Typography variant="body2" color="error" paddingTop={0.5}>
          {errors[key]}
        </Typography>
      )
    );
  }

  function toggleEvergreen() {
    setEvergreen((current) => {
      const next = !current;
      if (next) {
        setEndDate(null);
        setErrors((prev) => _.omit(prev, 'endDate'));
      }
      return next;
    });
  }

  function getPrimaryActionLabel() {
    if (loading) {
      return isEditing ? 'Saving...' : 'Creating...';
    }
    return isEditing ? 'Save' : 'Create template';
  }

  const showEndNowButton = shouldShowEndNowButton(initialTemplate, evergreen);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Stack spacing="20px">
        <Box>
          <Button
            color="inherit"
            disableRipple
            startIcon={<ArrowBack sx={styles.backIcon} />}
            size="small"
            sx={styles.backButton}
            onClick={handleCancel}
          >
            Templates
          </Button>
          <Typography component="h1" sx={styles.pageTitle}>
            {isEditing ? 'Edit template' : 'Create template'}
          </Typography>
        </Box>

        <Paper component="section" variant="outlined" sx={styles.section}>
          <Stack spacing="20px" padding="20px">
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ md: 'flex-start' }}
            >
              <Stack spacing={0.75} sx={styles.classificationField}>
                <FormLabel sx={styles.fieldLabel}>Select post type</FormLabel>
                <FormControl fullWidth>
                  <Select
                    value={template.category}
                    onChange={handleCategoryChange}
                    sx={styles.selectField}
                    displayEmpty
                    renderValue={(selected) =>
                      TEMPLATE_CATEGORIES.find(
                        (category) => category.id === selected
                      )?.name || 'None'
                    }
                  >
                    {TEMPLATE_CATEGORIES.map(({ id, name }) => (
                      <MenuItem key={id} value={id}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {renderError('category')}
              </Stack>

              <Stack spacing={0.75} sx={styles.classificationField}>
                <FormLabel sx={styles.fieldLabel}>Topic</FormLabel>
                <FormControl fullWidth>
                  <Select
                    value={
                      isShoppableSelected || addTopic
                        ? ''
                        : template.topic || ''
                    }
                    disabled={isShoppableSelected || addTopic}
                    onChange={(event) => {
                      setTemplate((current) => ({
                        ...current,
                        topic: event.target.value,
                        subtopic: '',
                      }));
                    }}
                    onClose={() => {
                      setAddTopic(false);
                      setNewTopic('');
                    }}
                    sx={styles.selectField}
                    displayEmpty
                    renderValue={(selected) => {
                      if (isShoppableSelected) return 'N/A for Product Links';
                      if (addTopic) return 'Add topic';
                      return topicsMap[selected]?.name || 'None';
                    }}
                  >
                    {localTopics.map(({ id, name }) => (
                      <MenuItem key={id} value={id}>
                        {name}
                      </MenuItem>
                    ))}
                    {!isShoppableSelected && !addTopic && (
                      <MenuItem
                        sx={{ fontWeight: 700 }}
                        onClickCapture={(event) => {
                          setAddTopic(true);
                          event.stopPropagation();
                        }}
                      >
                        + Add topic
                      </MenuItem>
                    )}
                    {addTopic && (
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        padding="6px 15px"
                      >
                        <TextField
                          autoFocus
                          value={newTopic || ''}
                          onChange={(event) => setNewTopic(event.target.value)}
                          variant="standard"
                          size="small"
                          onKeyDown={(event) => event.stopPropagation()}
                          disabled={topicLoading}
                          onClickCapture={(event) => event.stopPropagation()}
                        />
                        <IconButton
                          disabled={!newTopic || topicLoading}
                          onClick={handleCreateTopic}
                          color={newTopic ? 'success' : 'default'}
                        >
                          <CheckCircleOutline />
                        </IconButton>
                      </Box>
                    )}
                  </Select>
                </FormControl>
                {renderError('topic')}
              </Stack>

              <Stack spacing={0.75} sx={styles.classificationField}>
                <FormLabel sx={styles.fieldLabel}>Subtopic</FormLabel>
                <FormControl fullWidth>
                  <Select
                    disabled={
                      isShoppableSelected || !template.topic || addSubtopic
                    }
                    value={
                      isShoppableSelected || addSubtopic
                        ? ''
                        : template.subtopic || ''
                    }
                    onChange={(event) => {
                      setTemplate((current) => ({
                        ...current,
                        subtopic: event.target.value,
                      }));
                    }}
                    onClose={() => {
                      setAddSubtopic(false);
                      setNewSubtopic('');
                    }}
                    sx={styles.selectField}
                    displayEmpty
                    renderValue={(selected) => {
                      if (isShoppableSelected) return 'N/A for Product Links';
                      if (addSubtopic) return 'Add subtopic';
                      return (
                        subtopics.find((subtopic) => subtopic.id === selected)
                          ?.name || 'None'
                      );
                    }}
                  >
                    {subtopics.map(({ id, name }) => (
                      <MenuItem key={id} value={id}>
                        {name}
                      </MenuItem>
                    ))}
                    {!isShoppableSelected && !addSubtopic && template.topic && (
                      <MenuItem
                        sx={{ fontWeight: 700 }}
                        onClickCapture={(event) => {
                          setAddSubtopic(true);
                          event.stopPropagation();
                        }}
                      >
                        + Add subtopic
                      </MenuItem>
                    )}
                    {addSubtopic && (
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        padding="6px 15px"
                      >
                        <TextField
                          autoFocus
                          value={newSubtopic || ''}
                          onChange={(event) =>
                            setNewSubtopic(event.target.value)
                          }
                          variant="standard"
                          size="small"
                          onKeyDown={(event) => event.stopPropagation()}
                          disabled={subtopicLoading}
                          onClickCapture={(event) => event.stopPropagation()}
                        />
                        <IconButton
                          disabled={!newSubtopic || subtopicLoading}
                          onClick={handleCreateSubtopic}
                          color={newSubtopic ? 'success' : 'default'}
                        >
                          <CheckCircleOutline />
                        </IconButton>
                      </Box>
                    )}
                  </Select>
                </FormControl>
                {renderError('subtopic')}
              </Stack>
            </Stack>
          </Stack>
        </Paper>

        <Paper component="section" variant="outlined" sx={styles.section}>
          <Stack spacing="20px" padding="20px">
            <TemplateContentForm
              template={template}
              onChange={setTemplate}
              renderError={renderError}
            />
          </Stack>
        </Paper>

        <Paper component="section" variant="outlined" sx={styles.section}>
          <Stack spacing="20px" padding="20px">
            <TemplateTargetingForm
              key={initialTemplate?.id ?? 'new'}
              initialTargeting={initialTargetingValue}
              onStateChanged={handleTargetingChanged}
              isValidating={isValidating}
            />
          </Stack>
        </Paper>

        <Paper component="section" variant="outlined" sx={styles.section}>
          <Stack spacing="20px" padding="20px">
            <Typography component="h2" sx={styles.sectionTitle}>
              Scheduling
            </Typography>
            <Box sx={styles.schedulingGrid}>
              <Box sx={styles.schedulingLabelRow}>
                <FormLabel sx={styles.fieldLabel}>Start date</FormLabel>
              </Box>
              <Box sx={styles.schedulingEndLabelRow}>
                <FormLabel sx={styles.fieldLabel}>End date</FormLabel>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={toggleEvergreen}
                  sx={[
                    styles.evergreenButton,
                    evergreen ? styles.evergreenButtonSelected : null,
                  ]}
                >
                  ∞ Evergreen
                </Button>
              </Box>

              <Box>
                <DatePicker
                  format="MM/DD/YYYY"
                  value={startDate}
                  onError={(err) => handleDateError('startDate', err)}
                  onChange={(event) => {
                    if (!event) {
                      setStartDate(null);
                      return;
                    }

                    setStartDate(toCentralStartOfDay(event));
                    handleDateError('startDate', null);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      placeholder: 'mm/dd/yyyy (12:00 AM CT)',
                      error: Boolean(errors.startDate),
                      sx: styles.dateField,
                    },
                  }}
                />
                {renderError('startDate')}
              </Box>

              <Box>
                {evergreen ? (
                  <TextField
                    fullWidth
                    disabled
                    value="∞ mm/dd/yyyy (11:59 PM CT)"
                    sx={styles.evergreenField}
                  />
                ) : (
                  <>
                    <DatePicker
                      format="MM/DD/YYYY"
                      value={endDate}
                      minDate={startDate}
                      onError={(err) => handleDateError('endDate', err)}
                      onChange={(event) => {
                        if (!event) {
                          setEndDate(null);
                          return;
                        }

                        setEndDate(toCentralEndOfDay(event));
                        handleDateError('endDate', null);
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          placeholder: 'mm/dd/yyyy (11:59 PM CT)',
                          error: Boolean(errors.endDate),
                          sx: styles.dateField,
                        },
                      }}
                    />
                    {renderError('endDate')}
                  </>
                )}
              </Box>
            </Box>
          </Stack>
        </Paper>

        <Paper component="footer" variant="outlined" sx={styles.section}>
          <Stack
            direction="row"
            spacing={1.5}
            justifyContent="space-between"
            alignItems="center"
            padding="16px 20px"
          >
            <Box>
              {showEndNowButton && (
                <Button
                  type="button"
                  variant="text"
                  sx={styles.endNowButton}
                  onClick={handleDeleteTemplate}
                  disabled={loading}
                  startIcon={<Delete />}
                >
                  End now
                </Button>
              )}
            </Box>
            <Stack direction="row" spacing={1.5}>
              <Button
                type="button"
                variant="outlined"
                onClick={handleCancel}
                disabled={loading}
                sx={styles.secondaryButton}
              >
                {isEditing ? 'Discard changes' : 'Cancel'}
              </Button>
              <Button
                type="button"
                variant="contained"
                onClick={onSubmit}
                disabled={loading}
                startIcon={
                  loading ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : null
                }
                sx={styles.primaryButton}
              >
                {getPrimaryActionLabel()}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Stack>
    </LocalizationProvider>
  );
}

TemplateForm.propTypes = {
  initialTemplate: PropTypes.shape({
    id: PropTypes.string,
    start_date: PropTypes.string,
    end_date: PropTypes.string,
    targeting: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  }),
  topics: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onBack: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  onTopicsChanged: PropTypes.func.isRequired,
};

TemplateForm.defaultProps = {
  initialTemplate: null,
};
